"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import Image from "next/image";
import { Check } from "lucide-react";
import { ButtonLink } from "@/components/ui/button-link";

interface DownloadHeroProps {
  title: {
    normal: string;
    highlight: string;
  };
  description: string;
  benefits: string[];
  primaryCta: string;
  primaryCtaHref?: string;
  secondaryCta?: string;
  secondaryCtaHref?: string;
  disclaimer: string;
  image: string;
}

const DownloadHero = ({
  title,
  description,
  benefits,
  primaryCta,
  primaryCtaHref,
  secondaryCta,
  secondaryCtaHref,
  disclaimer,
  image,
}: DownloadHeroProps) => {
  return (
    <section className="relative pt-28 pb-0 bg-white min-h-[90vh] flex flex-col items-center overflow-visible">
      <div className="container max-w-[1600px] mx-auto px-4 lg:px-6 relative z-20">
        {/* Main Hero Card - Matching Homepage Island Size */}
        <div className="bg-foreground rounded-[3rem] lg:rounded-[4rem] relative overflow-visible flex flex-col lg:flex-row min-h-[85vh] lg:min-h-[90vh] shadow-2xl">
          
          {/* Left Side: Content - 2/3 width to ensure image centers over transition */}
          <div className="lg:flex-[2] px-8 pt-16 pb-64 lg:p-24 lg:pr-0 flex flex-col justify-center items-center lg:items-start text-center lg:text-left gap-6 relative z-10">
            <div className="space-y-3 max-w-xl">
              <BlurFade delay={0.1}>
                <h1 className="text-3xl md:text-5xl lg:text-[4.5rem] font-bold tracking-tight leading-[1.05] text-white">
                  {title.normal} <br />
                  <span className="text-secondary italic">{title.highlight}</span>
                </h1>
              </BlurFade>

              <BlurFade delay={0.2}>
                <p className="text-base lg:text-lg text-white/70 font-medium">
                  {description}
                </p>
              </BlurFade>
            </div>

            <div className="flex flex-col items-start space-y-3 mx-auto lg:ml-0">
              {benefits.map((benefit, i) => (
                <BlurFade
                  key={i}
                  delay={0.3 + i * 0.1}
                  className="flex items-center gap-3 text-white/80 font-medium text-sm lg:text-lg text-left"
                >
                  <div className="w-5 h-5 rounded-full border border-white/20 flex items-center justify-center shrink-0">
                    <Check size={10} className="text-secondary" />
                  </div>
                  {benefit}
                </BlurFade>
              ))}
            </div>

            <BlurFade
              delay={0.6}
              className="flex flex-col sm:flex-row items-center lg:items-start gap-4 pt-4 w-full max-w-md lg:max-w-none"
            >
              <ButtonLink
                href={primaryCtaHref}
                variant="primary-secondary"
                size="md"
                className="w-full lg:w-auto font-black"
              >
                {primaryCta}
              </ButtonLink>
              {secondaryCta && secondaryCtaHref && (
                <ButtonLink
                  href={secondaryCtaHref}
                  variant="secondary"
                  size="md"
                  className="w-full lg:w-auto"
                >
                  {secondaryCta}
                </ButtonLink>
              )}
            </BlurFade>

            <BlurFade delay={0.7}>
              <p className="text-[10px] font-bold tracking-wider text-white/40 uppercase">
                {disclaimer}
              </p>
            </BlurFade>
          </div>

          {/* Right Side: Teal Background - 1/3 width */}
          <div className="lg:flex-1 min-h-[220px] lg:min-h-0 relative bg-secondary rounded-b-[3rem] lg:rounded-b-none lg:rounded-r-[4rem] overflow-hidden">
            {/* Background Radial Waves */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden flex items-center justify-center">
              <div className="absolute w-[160%] aspect-square border-[1px] border-white/30 rounded-full" />
              <div className="absolute w-[130%] aspect-square border-[1px] border-white/20 rounded-full" />
              <div className="absolute w-[100%] aspect-square border-[1px] border-white/10 rounded-full" />
              <div className="absolute w-[70%] aspect-square border-[1px] border-white/5 rounded-full" />
            </div>

            {/* Creative Wavy Line Art */}
            <div className="absolute inset-0 pointer-events-none z-10 opacity-40">
              {/* Refined wavy lines matching screenshot */}
              <div className="absolute top-[40%] -left-20 w-64 h-64 border-[1.5px] border-white rounded-full opacity-20" />
              <div className="absolute top-[20%] right-[-10%] w-48 h-48 border-[1px] border-white/30 rounded-full" />
            </div>
          </div>

          {/* The Phone Image - Perfectly centered over the color transition */}
          {image && (
            <div
              className="absolute left-1/2 lg:left-[66.6%] -translate-x-1/2 bottom-0 h-[80%] w-[80%] lg:w-[40%] pointer-events-none z-20 overflow-visible"
              style={{ clipPath: "inset(-100% -100% 0 -100%)" }}
            >
              <Image
                src={image}
                alt="Tiera App on Phone"
                fill
                className="object-contain object-bottom origin-bottom"
                priority
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DownloadHero;
